import { HcpVerificationStatus as RankedHcpVerificationStatus } from '@heidi/schema/ml-scribe';

import { useQueryHcpVerifications } from './use-query-hcp-verifications';

/**
 * - `ineligible`: nothing to show — flag OFF, non-US, unresolved user, no
 *   session token, backend gate (403), or any fetch failure.
 * - `loading`: first read in flight; render nothing rather than flash a banner.
 * - `unverified`: eligible with no record — never attempted.
 * - `pending_review`: a `NEEDS_REVIEW` record exists; the user has already
 *   acted, so no nag.
 * - `verified`: a `VERIFIED` record.
 * - `rejected`: a reviewer declined; treated like `unverified` for placement
 *   and nag, with its own copy.
 */
export type HcpVerificationState =
  | 'ineligible'
  | 'loading'
  | 'unverified'
  | 'pending_review'
  | 'verified'
  | 'rejected';

export interface ResolveHcpVerificationStateInput {
  isEnabled: boolean;
  isPending: boolean;
  isError: boolean;
  status: RankedHcpVerificationStatus | null | undefined;
}

/**
 * The backend has already ranked the records (`VERIFIED` > `NEEDS_REVIEW` >
 * `REJECTED` > none); this only names the result. A status this build does
 * not know is `ineligible`, so a new server value hides the prompt rather
 * than nagging.
 */
export const resolveHcpVerificationState = ({
  isEnabled,
  isPending,
  isError,
  status,
}: ResolveHcpVerificationStateInput): HcpVerificationState => {
  if (!isEnabled) return 'ineligible';
  if (isError) return 'ineligible';
  if (isPending || status === undefined) return 'loading';
  switch (status) {
    case null:
      return 'unverified';
    case RankedHcpVerificationStatus.VERIFIED:
      return 'verified';
    case RankedHcpVerificationStatus.NEEDS_REVIEW:
      return 'pending_review';
    case RankedHcpVerificationStatus.REJECTED:
      return 'rejected';
    default:
      return 'ineligible';
  }
};

export interface HcpVerificationStatus {
  state: HcpVerificationState;
}

/**
 * Single seam every HCP-verification surface reads. The server ranks the
 * records; the client only maps that status to a state.
 */
export function useHcpVerificationStatus(): HcpVerificationStatus {
  const query = useQueryHcpVerifications();

  return {
    state: resolveHcpVerificationState({
      isEnabled: query.isEnabled,
      isPending: query.isPending,
      isError: query.isError,
      status: query.data,
    }),
  };
}
